import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { GlassCard } from "./GlassCard";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Zap, User, Mail, Lock, UserPlus, LogIn, ArrowLeft, Globe, Check, X, AtSign, Sparkles, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import { MoodPunkLogo } from "./MoodPunkLogo";
import { MilestoneCelebration, useMilestoneCelebration } from "./MilestoneConfetti";

// Animated Counter Component
const AnimatedCounter = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    if (value === 0) return;
    
    const duration = 1500;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <span className="font-orbitron font-bold text-neon-cyan">
      {displayValue.toLocaleString()}
    </span>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Selector Component
const LanguageSelector = ({ onSelect }) => {
  const { language, setLanguage, languages, t } = useLanguage();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full"
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neon-cyan/20 border border-neon-cyan/50 flex items-center justify-center">
          <Globe className="w-8 h-8 text-neon-cyan" />
        </div>
        <h2 className="text-2xl font-orbitron text-white mb-2">
          {t("login.selectLanguage")}
        </h2>
      </div>
      
      <div className="grid grid-cols-1 gap-3">
        {Object.entries(languages).map(([code, { name, flag }]) => (
          <motion.button
            key={code}
            onClick={() => {
              setLanguage(code);
              onSelect();
            }}
            className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
              language === code
                ? "bg-neon-cyan/20 border-neon-cyan/50 text-neon-cyan"
                : "bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{flag}</span>
              <span className="font-rajdhani font-semibold">{name}</span>
            </div>
            {language === code && (
              <Check className="w-5 h-5 text-neon-cyan" />
            )}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

export const LoginPage = () => {
  const { loginWithGoogle, loginAsIncognito, registerMoodPunk, loginMoodPunk, isAuthenticated, initialized } = useAuth();
  const { t, languages, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [view, setView] = useState("language");
  const [loading, setLoading] = useState(false);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [userIdStatus, setUserIdStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [userIdError, setUserIdError] = useState("");
  const [idSuggestions, setIdSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [userCount, setUserCount] = useState(0);

  // Milestone celebration hook
  const { showCelebration, celebratedMilestone, closeCelebration } = useMilestoneCelebration(userCount);

  // Fetch user count on mount
  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const response = await axios.get(`${API}/users/count`);
        setUserCount(response.data.count || 0);
      } catch (error) {
        console.error("Error fetching user count:", error);
      }
    };
    fetchUserCount();
  }, []);

  useEffect(() => {
    const savedLang = localStorage.getItem("moodpunk_language");
    if (savedLang) {
      setView("main");
    }
  }, []);

  useEffect(() => {
    if (initialized && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [initialized, isAuthenticated, navigate]);

  // Debounced ID check
  useEffect(() => {
    if (!userId || userId.length < 3) {
      setUserIdStatus(null);
      setUserIdError("");
      setIdSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setUserIdStatus('checking');
      try {
        const response = await axios.get(`${API}/auth/check-id/${userId}`);
        if (response.data.available) {
          setUserIdStatus('available');
          setUserIdError("");
          setIdSuggestions([]);
        } else {
          setUserIdStatus('taken');
          setUserIdError(response.data.error || "ID no disponible");
          // Fetch suggestions
          fetchSuggestions(userId);
        }
      } catch (error) {
        setUserIdStatus('invalid');
        setUserIdError("Error al verificar ID");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [userId]);

  const fetchSuggestions = async (baseId) => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/auth/suggest-ids/${baseId}`);
      setIdSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setIdSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setUserId(suggestion);
    setIdSuggestions([]);
  };

  const handleIncognito = () => {
    loginAsIncognito();
    navigate("/", { replace: true });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error(t("common.error"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("common.error"));
      return;
    }
    // Validate user ID if provided
    if (userId && userIdStatus !== 'available') {
      toast.error("Por favor elige un ID de usuario válido");
      return;
    }
    
    setLoading(true);
    try {
      const result = await registerMoodPunk(email, password, name, userId || null);
      toast.success(t("common.success"));
      navigate("/", { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || t("common.error");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t("common.error"));
      return;
    }
    
    setLoading(true);
    try {
      await loginMoodPunk(email, password);
      toast.success(t("common.success"));
      navigate("/", { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || t("common.error");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setUserId("");
    setUserIdStatus(null);
    setUserIdError("");
    setIdSuggestions([]);
  };

  return (
    <div className="min-h-screen bg-void flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neon-purple/20 via-transparent to-transparent pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-neon-cyan/10 via-transparent to-transparent pointer-events-none" />
      
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent"
            style={{ top: `${20 + i * 15}%`, left: 0, right: 0 }}
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        <GlassCard className="p-8" neon>
          <AnimatePresence mode="wait">
            {view === "language" && (
              <LanguageSelector key="language" onSelect={() => setView("main")} />
            )}

            {view === "main" && (
              <motion.div
                key="main"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 border border-neon-cyan/30 mb-4"
                  >
                    <Zap className="w-10 h-10 text-neon-cyan" />
                  </motion.div>
                  <MoodPunkLogo size="lg" animate className="mb-2" />
                  <p className="text-slate-400 font-rajdhani">
                    {t("login.subtitle")}
                  </p>
                  
                  {/* User Counter */}
                  {userCount > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="mt-4 flex items-center justify-center gap-2 text-sm"
                    >
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-neon-cyan/10 border border-neon-cyan/20">
                        <Users className="w-4 h-4 text-neon-cyan" />
                        <span className="text-slate-400 font-rajdhani">
                          <AnimatedCounter value={userCount} /> {t("login.usersJoined") || "usuarios registrados"}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Language indicator */}
                <button
                  onClick={() => setView("language")}
                  className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-sm"
                >
                  <span>{languages[language]?.flag}</span>
                  <span className="text-slate-400 font-rajdhani">{languages[language]?.name}</span>
                </button>

                <div className="space-y-3">
                  <Button
                    onClick={() => { resetForm(); setView("login"); }}
                    data-testid="login-moodpunk-btn"
                    className="w-full py-5 font-rajdhani font-bold uppercase tracking-widest bg-neon-cyan/10 hover:bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30 hover:border-neon-cyan/50 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 hover:shadow-[0_0_20px_rgba(0,243,255,0.3)]"
                  >
                    <Zap className="w-5 h-5" />
                    {t("login.moodpunk")}
                  </Button>

                  <Button
                    onClick={loginWithGoogle}
                    data-testid="login-google-btn"
                    className="w-full py-5 font-rajdhani font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 text-white border border-white/20 hover:border-white/40 rounded-xl transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t("login.google")}
                  </Button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-void-paper px-3 text-slate-500 font-rajdhani tracking-wider">{t("login.or")}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleIncognito}
                    data-testid="login-incognito-btn"
                    className="w-full py-5 font-rajdhani font-bold uppercase tracking-widest bg-neon-purple/10 hover:bg-neon-purple/20 text-neon-purple border border-neon-purple/30 hover:border-neon-purple/50 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 hover:shadow-[0_0_20px_rgba(188,19,254,0.3)]"
                  >
                    <User className="w-5 h-5" />
                    {t("login.incognito")}
                  </Button>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-xs text-slate-500 font-inter leading-relaxed">
                    {t("login.incognitoDesc")} • {t("login.googleDesc")}
                  </p>
                </div>
              </motion.div>
            )}

            {view === "login" && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <button
                  onClick={() => setView("main")}
                  className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="font-rajdhani">{t("common.cancel")}</span>
                </button>

                <div className="text-center mb-6">
                  <LogIn className="w-12 h-12 text-neon-cyan mx-auto mb-3" />
                  <h2 className="font-orbitron text-xl text-white">{t("login.login")}</h2>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="email"
                      placeholder={t("login.email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-black/30 border-white/10 text-white"
                      data-testid="login-email"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="password"
                      placeholder={t("login.password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-black/30 border-white/10 text-white"
                      data-testid="login-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 cyber-button-cyan"
                    data-testid="login-submit"
                  >
                    {loading ? t("common.loading") : t("login.login")}
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-slate-400">
                  {t("login.noAccount")}{" "}
                  <button
                    onClick={() => { resetForm(); setView("register"); }}
                    className="text-neon-cyan hover:underline"
                  >
                    {t("login.createAccount")}
                  </button>
                </p>
              </motion.div>
            )}

            {view === "register" && (
              <motion.div
                key="register"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <button
                  onClick={() => setView("main")}
                  className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="font-rajdhani">{t("common.cancel")}</span>
                </button>

                <div className="text-center mb-6">
                  <UserPlus className="w-12 h-12 text-neon-purple mx-auto mb-3" />
                  <h2 className="font-orbitron text-xl text-white">{t("login.register")}</h2>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="text"
                      placeholder={t("login.name")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-black/30 border-white/10 text-white"
                      data-testid="register-name"
                    />
                  </div>
                  
                  {/* User ID field with validation and suggestions */}
                  <div className="space-y-2">
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="text"
                        placeholder="Tu ID único (ej: maria_punk)"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                        className={`pl-10 pr-10 bg-black/30 text-white ${
                          userIdStatus === 'available' ? 'border-green-500/50' :
                          userIdStatus === 'taken' || userIdStatus === 'invalid' ? 'border-red-500/50' :
                          'border-white/10'
                        }`}
                        data-testid="register-userid"
                        maxLength={20}
                      />
                      {/* Status indicator */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {userIdStatus === 'checking' && (
                          <div className="w-5 h-5 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                        )}
                        {userIdStatus === 'available' && (
                          <Check className="w-5 h-5 text-green-400" />
                        )}
                        {(userIdStatus === 'taken' || userIdStatus === 'invalid') && (
                          <X className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Helper text */}
                    <p className="text-xs text-slate-500 pl-1">
                      {userId.length === 0 ? (
                        "Opcional: 3-20 caracteres, letras, números y _"
                      ) : userIdStatus === 'available' ? (
                        <span className="text-green-400">¡ID disponible!</span>
                      ) : userIdError ? (
                        <span className="text-red-400">{userIdError}</span>
                      ) : userId.length < 3 ? (
                        "Mínimo 3 caracteres"
                      ) : null}
                    </p>
                    
                    {/* ID Suggestions */}
                    {idSuggestions.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 rounded-lg bg-neon-purple/10 border border-neon-purple/30"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-neon-purple" />
                          <span className="text-xs text-neon-purple font-rajdhani font-semibold">
                            IDs sugeridos disponibles:
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {idSuggestions.map((suggestion) => (
                            <motion.button
                              key={suggestion}
                              type="button"
                              onClick={() => selectSuggestion(suggestion)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="px-3 py-1 rounded-full bg-white/10 hover:bg-neon-purple/30 border border-white/20 hover:border-neon-purple/50 text-sm text-white font-rajdhani transition-all"
                            >
                              @{suggestion}
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                    
                    {loadingSuggestions && (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <div className="w-3 h-3 border border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                        <span>Buscando IDs disponibles...</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="email"
                      placeholder={t("login.email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-black/30 border-white/10 text-white"
                      data-testid="register-email"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="password"
                      placeholder={t("login.password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-black/30 border-white/10 text-white"
                      data-testid="register-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || (userId && userIdStatus !== 'available' && userIdStatus !== null)}
                    className="w-full py-5 cyber-button-purple"
                    data-testid="register-submit"
                  >
                    {loading ? t("common.loading") : t("login.register")}
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-slate-400">
                  {t("login.hasAccount")}{" "}
                  <button
                    onClick={() => { resetForm(); setView("login"); }}
                    className="text-neon-purple hover:underline"
                  >
                    {t("login.loginHere")}
                  </button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </GlassCard>
      </motion.div>
      
      {/* Milestone Celebration Overlay */}
      <AnimatePresence>
        {showCelebration && celebratedMilestone && (
          <MilestoneCelebration 
            milestone={celebratedMilestone} 
            onClose={closeCelebration} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};
